import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import useAuth from "@/hooks/useAuth";
import equitiesSSEClient from "@/shared/api/firehose/equitiesSSEClient";
import { fetchCalendarEventsByDay } from ".";
import {
  CalendarEvent,
  isCalendarEventVisible,
} from "@/shared/models/economic-calendar/CalendarEvent";

export const useGetCalendarEvents = (d: Date) => {
  const { accessToken } = useAuth();
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;

  const queryClient = useQueryClient();
  const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const query = useQuery({
    queryKey: ["calendarEvents", dateKey],
    queryFn: () => fetchCalendarEventsByDay(d),
    staleTime: Infinity,
  });

  // Keep stored SSE params up to date when the token refreshes,
  // so error-triggered reconnects use the latest token without
  // tearing down the existing connection.
  useEffect(() => {
    if (accessToken) {
      equitiesSSEClient.updateUrlParams({ token: accessToken });
    }
  }, [accessToken]);

  // SSE subscription for real-time calendar event updates
  useEffect(() => {
    if (!query.isSuccess || !accessTokenRef.current) return;

    equitiesSSEClient.start({
      urlParams: { token: accessTokenRef.current },
    });

    const handler = (item: any) => {
      const patch = item as Partial<CalendarEvent> & { uuid?: string };
      if (!patch?.uuid) return; // require stable key

      // Merge/patch into the current day; also drop if the event moved off this day
      queryClient.setQueryData(
        ["calendarEvents", dateKey],
        (oldData: CalendarEvent[] | undefined) => {
          const old = oldData ?? [];
          const existingIndex = old.findIndex((e) => e.uuid === patch.uuid);
          const prev = existingIndex >= 0 ? old[existingIndex] : undefined;

          // prefer incoming occurs_at, fall back to previous if omitted
          const occursAt = patch.occurs_at ?? prev?.occurs_at;
          if (!occursAt) return oldData; // can't place yet

          const evDate = new Date(occursAt).toISOString().split("T")[0];

          // If it moved to a different day, remove it from this day's cache if present
          if (evDate !== dateKey) {
            if (existingIndex >= 0) {
              const next = old.slice();
              next.splice(existingIndex, 1);
              return next;
            }
            return oldData;
          }

          // Upsert (merge patch over previous to avoid "untitled" duplicates)
          const merged = { ...prev, ...patch, occurs_at: occursAt } as CalendarEvent;

          // Filter out non-visible events (invisible, unknown)
          if (!isCalendarEventVisible(merged)) {
            if (existingIndex >= 0) {
              const next = old.slice();
              next.splice(existingIndex, 1);
              return next;
            }
            return oldData;
          }

          if (existingIndex >= 0) {
            const next = old.slice();
            next[existingIndex] = merged;
            return next;
          } else {
            return [...old, merged];
          }
        }
      );

      // Optional: if occurs_at points to a different day, also upsert into that day so it appears there
      if (patch.occurs_at) {
        const newKey = new Date(patch.occurs_at).toISOString().split("T")[0];
        if (newKey !== dateKey) {
          queryClient.setQueryData(
            ["calendarEvents", newKey],
            (oldData: CalendarEvent[] | undefined) => {
              const old = oldData ?? [];
              const idx = old.findIndex((e) => e.uuid === patch.uuid);
              const merged = {
                ...(idx >= 0 ? old[idx] : {}),
                ...patch,
              } as CalendarEvent;

              // Filter out non-visible events (invisible, unknown)
              if (!isCalendarEventVisible(merged)) {
                if (idx >= 0) {
                  const next = old.slice();
                  next.splice(idx, 1);
                  return next;
                }
                return oldData;
              }

              if (idx >= 0) {
                const next = old.slice();
                next[idx] = merged;
                return next;
              } else {
                return [...old, merged];
              }
            }
          );
        }
      }
    };

    const wrappedHandler = (parsed: unknown) => {
      const msg = parsed as Record<string, unknown>;
      if (msg.backend_source !== "newsquawk-calendar-event") return;
      handler(msg);
    };

    const cleanup = equitiesSSEClient.subscribe("message", wrappedHandler);

    return cleanup;
  }, [query.isSuccess, queryClient, dateKey]);

  // Memoize sorted data to prevent unnecessary re-renders
  const sortedData = useMemo(() => {
    return query.data
      ?.slice()
      .sort(
        (a, b) =>
          new Date(a.occurs_at).getTime() - new Date(b.occurs_at).getTime()
      );
  }, [query.data]);

  return {
    ...query,
    data: sortedData,
  };
};
